import { useEffect, useMemo, useState } from 'react';
import { LoaderCircle, Power, RefreshCw, Save } from 'lucide-react';
import { engineFetchJson } from '@/lib/engineHttp';

interface SkillEntry {
  id: string;
  tenant_id: string;
  name: string;
  slug: string;
  version: string;
  description: string | null;
  is_local: boolean;
  entry_point: string;
  config: Record<string, unknown>;
  content_hash: string | null;
  source_format: string;
  governor_profile: string | null;
  is_active: boolean;
  data_reads: string | null;
  created_at: string;
  updated_at: string | null;
  last_run_at: string | null;
}

function prettyJson(value: Record<string, unknown>): string {
  return JSON.stringify(value ?? {}, null, 2);
}

function readJsonConfig(raw: string): Record<string, unknown> {
  const parsed = JSON.parse(raw);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('Config must be a JSON object.');
  }
  return parsed as Record<string, unknown>;
}

export function SkillsPage() {
  const [skills, setSkills] = useState<SkillEntry[]>([]);
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [configDrafts, setConfigDrafts] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [actionSlug, setActionSlug] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await engineFetchJson<SkillEntry[]>('/skills');
      setSkills(data);
      setConfigDrafts(Object.fromEntries(data.map((skill) => [skill.slug, prettyJson(skill.config)])));
      setSelectedSlug((current) => current ?? data[0]?.slug ?? null);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Failed to load skills');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const selected = useMemo(
    () => skills.find((skill) => skill.slug === selectedSlug) ?? skills[0] ?? null,
    [selectedSlug, skills],
  );

  const enabledCount = skills.filter((skill) => skill.is_active).length;

  const patchSkill = async (slug: string, body: Record<string, unknown>) => {
    setActionSlug(slug);
    setError(null);
    setNotice(null);
    try {
      const updated = await engineFetchJson<SkillEntry>(`/skills/${slug}/config`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      setSkills((current) =>
        current.map((skill) =>
          skill.slug === slug
            ? {
                ...skill,
                ...updated,
                data_reads: skill.data_reads,
              }
            : skill,
        ),
      );
      if ('config' in body) {
        setConfigDrafts((current) => ({ ...current, [slug]: prettyJson(updated.config) }));
      }
      setNotice(`${updated.name} updated.`);
    } catch (patchError) {
      setError(patchError instanceof Error ? patchError.message : 'Failed to update skill');
    } finally {
      setActionSlug(null);
    }
  };

  const toggleSkill = (skill: SkillEntry) => {
    void patchSkill(skill.slug, { is_active: !skill.is_active });
  };

  const saveConfig = (skill: SkillEntry) => {
    try {
      const config = readJsonConfig(configDrafts[skill.slug] ?? '{}');
      void patchSkill(skill.slug, { config });
    } catch (parseError) {
      setError(parseError instanceof Error ? parseError.message : 'Invalid JSON config.');
    }
  };

  if (loading) {
    return (
      <div className="sp-page">
        <div className="sp-widget">
          <div className="sp-widget-body flex items-center gap-2 text-sm text-gray-400">
            <LoaderCircle className="h-4 w-4 animate-spin" />
            Loading Skills shelf...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sp-page space-y-4">
      <div className="sp-widget">
        <div className="sp-widget-header flex items-center justify-between">
          <div>
            <div className="mono text-xs uppercase tracking-[0.24em] text-gray-500">Skills</div>
            <h1 className="text-lg font-semibold text-gray-100">Tenant Skill Shelf</h1>
          </div>
          <button className="sp-button" onClick={() => void refresh()} type="button">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
        <div className="sp-widget-body">
          <div className="grid gap-3 text-xs text-gray-400 sm:grid-cols-3">
            <div>
              <span className="mono text-gray-500">Installed</span>
              <div className="text-lg text-gray-100">{skills.length}</div>
            </div>
            <div>
              <span className="mono text-gray-500">Enabled</span>
              <div className="text-lg text-[#39FF14]">{enabledCount}</div>
            </div>
            <div>
              <span className="mono text-gray-500">Contract</span>
              <div className="text-sm text-gray-300">GET /skills · PATCH /skills/:slug/config</div>
            </div>
          </div>
          {notice && <div className="mt-3 text-xs text-[#39FF14]">{notice}</div>}
          {error && <div className="mt-3 text-xs text-red-400">{error}</div>}
        </div>
      </div>

      {skills.length === 0 ? (
        <div className="sp-widget">
          <div className="sp-widget-body text-sm text-gray-400">No registered skills were returned by the engine.</div>
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[360px_1fr]">
          <div className="sp-widget">
            <div className="sp-widget-header mono text-xs uppercase tracking-[0.18em] text-gray-500">
              Installed Skills
            </div>
            <div className="sp-widget-body space-y-2">
              {skills.map((skill) => (
                <button
                  key={skill.slug}
                  className={`w-full rounded border px-3 py-2 text-left transition ${
                    selected?.slug === skill.slug
                      ? 'border-[#39FF14]/50 bg-[#39FF14]/5'
                      : 'border-gray-800 bg-gray-950/40 hover:border-gray-700'
                  }`}
                  onClick={() => setSelectedSlug(skill.slug)}
                  type="button"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm text-gray-100">{skill.name}</span>
                    <span className={`mono text-[10px] ${skill.is_active ? 'text-[#39FF14]' : 'text-gray-500'}`}>
                      {skill.is_active ? 'ON' : 'OFF'}
                    </span>
                  </div>
                  <div className="mono mt-1 text-[10px] text-gray-500">{skill.slug}</div>
                </button>
              ))}
            </div>
          </div>

          {selected && (
            <div className="sp-widget">
              <div className="sp-widget-header flex items-center justify-between">
                <div>
                  <div className="mono text-xs text-gray-500">{selected.slug}</div>
                  <h2 className="text-base font-semibold text-gray-100">{selected.name}</h2>
                </div>
                <button
                  aria-label={`${selected.is_active ? 'Disable' : 'Enable'} ${selected.name}`}
                  className={`sp-button ${selected.is_active ? 'border-[#39FF14]/50 text-[#39FF14]' : ''}`}
                  disabled={actionSlug === selected.slug}
                  onClick={() => toggleSkill(selected)}
                  type="button"
                >
                  <Power className="h-4 w-4" />
                  {selected.is_active ? 'Enabled' : 'Disabled'}
                </button>
              </div>
              <div className="sp-widget-body space-y-4">
                <p className="text-sm text-gray-300">{selected.description ?? 'No description provided.'}</p>
                <div className="rounded border border-gray-800 bg-gray-950/50 p-3">
                  <div className="mono mb-1 text-[10px] uppercase tracking-[0.18em] text-gray-500">Data Reads</div>
                  <div className="text-sm text-gray-300">{selected.data_reads ?? 'No data-read declaration.'}</div>
                </div>
                <div>
                  <label className="mono mb-2 block text-[10px] uppercase tracking-[0.18em] text-gray-500">
                    Config JSON
                  </label>
                  <textarea
                    aria-label={`${selected.name} config JSON`}
                    className="h-48 w-full rounded border border-gray-800 bg-black/40 p-3 font-mono text-xs text-gray-200 outline-none focus:border-[#39FF14]/60"
                    onChange={(event) =>
                      setConfigDrafts((current) => ({ ...current, [selected.slug]: event.target.value }))
                    }
                    value={configDrafts[selected.slug] ?? '{}'}
                  />
                  <button
                    className="sp-button mt-2"
                    disabled={actionSlug === selected.slug}
                    onClick={() => saveConfig(selected)}
                    type="button"
                  >
                    <Save className="h-4 w-4" />
                    Save Config
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
